import logo from "@/assets/logo.png";
import portalImage from "@/assets/portal.png";
import { ThemeProvider } from "@/components/theme-provider";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getEnv } from "@/lib/get_env";
import { Blocks, Download, Key, LogIn } from "lucide-react";
import { useAuth } from "react-oidc-context";

const SITE_NAME = getEnv("VITE_SITE_NAME") || "Istari Customer Portal";

const features = [
  {
    icon: Download,
    title: "Software Assets",
    description:
      "Download infrastructure packages, Helm charts, Docker images, the Istari Agent, and utility tools for your deployment.",
  },
  {
    icon: Blocks,
    title: "Integrations",
    description:
      "Access modules that connect the platform to engineering tools like Dassault 3DExperience, MSC NASTRAN, and PTC Creo.",
  },
  {
    icon: Key,
    title: "License Management",
    description: "Generate, track, and manage your Istari Platform licenses across your organization.",
  },
];

const navLinks = [{ label: "Home", href: "#" }];

export function LandingPage() {
  const auth = useAuth();

  const handleLogin = () => {
    auth.signinRedirect({ state: { from: "/" } });
  };

  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <div className="flex min-h-screen flex-col bg-background font-sans text-foreground">
        <header className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur-md">
          <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-8">
              <a href="https://www.istaridigital.com/" target="_blank" rel="noopener noreferrer">
                <img src={logo} alt={SITE_NAME} className="h-8 dark:invert" />
              </a>
              <nav className="hidden items-center gap-6 md:flex">
                {navLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
                    {...(link.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button
                onClick={handleLogin}
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-brand-flight dark:bg-brand-digital dark:text-brand-black dark:hover:bg-brand-digital/80"
              >
                <LogIn />
                Log In
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1">
          <section className="relative overflow-hidden py-24 sm:py-32">
            <div
              className="pointer-events-none absolute inset-0 -z-10 opacity-40 dark:opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(ellipse 80% 50% at 50% -20%, hsl(var(--hero-gradient-spot) / 0.4), transparent)",
              }}
            />
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="grid items-center gap-12 lg:grid-cols-2">
                <div className="flex flex-col gap-6">
                  <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
                    Welcome to <span className="text-brand-flight dark:text-brand-digital">{SITE_NAME}</span>
                  </h1>
                  <p className="max-w-xl text-lg text-muted-foreground sm:text-xl">
                    The central hub for IT administrators and platform operators to download software, manage
                    integrations, and administer licenses for the Istari Digital Platform.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-2">
                    <Button
                      size="lg"
                      onClick={handleLogin}
                      className="bg-primary text-primary-foreground hover:bg-brand-flight dark:bg-brand-digital dark:text-brand-black dark:hover:bg-brand-digital/80"
                    >
                      <LogIn />
                      Log In
                    </Button>
                    <Button
                      size="lg"
                      variant="outline"
                      className="border-primary/30 text-primary hover:bg-brand-cloud dark:border-brand-digital/30 dark:text-brand-digital dark:hover:bg-brand-digital/10"
                      onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
                    >
                      Learn More
                    </Button>
                  </div>
                </div>

                <div className="hidden lg:block">
                  <img
                    src={portalImage}
                    alt="Customer Portal"
                    className="rounded-xl border border-brand-cloud shadow-lg dark:border-primary/40"
                  />
                </div>
              </div>
            </div>
          </section>

          <section id="features" className="border-t bg-brand-cloud/20 py-12 dark:bg-primary/10 sm:py-16">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                  Everything you need, in one place
                </h2>
                <p className="mt-3 text-lg text-muted-foreground">
                  Infrastructure, agents, integrations, and licenses — all from a single portal.
                </p>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {features.map((feature) => (
                  <Card key={feature.title} className="transition-shadow hover:shadow-md">
                    <CardHeader>
                      <div className="mb-2 flex size-12 items-center justify-center rounded-lg bg-brand-cloud dark:bg-primary/30">
                        <feature.icon className="size-6 text-brand-flight dark:text-brand-digital" />
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                      <CardDescription>{feature.description}</CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <section id="support" className="border-t py-10 sm:py-12">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
              <div className="mx-auto max-w-2xl text-center">
                <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Need Help?</h2>
                <p className="mt-3 text-muted-foreground">
                  Our team is here to assist you. Contact us at{" "}
                  <a
                    href="mailto:support@istaridigital.zendesk.com"
                    className="font-medium text-brand-flight underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-brand-digital"
                  >
                    support@istaridigital.zendesk.com
                  </a>
                </p>
              </div>
            </div>
          </section>
        </main>

        <footer className="border-t bg-brand-cloud/20 dark:bg-primary/10">
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                <a
                  href="https://www.istaridigital.com/about"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  About
                </a>
                <Separator orientation="vertical" className="h-4" />
                <a
                  href="https://www.istaridigital.com/eula"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  EULA
                </a>
                <Separator orientation="vertical" className="h-4" />
                <a
                  href="https://www.istaridigital.com/mla"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  MLA
                </a>
                <Separator orientation="vertical" className="h-4" />
                <a
                  href="https://www.istaridigital.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-primary"
                >
                  Privacy Policy
                </a>
              </div>
              <p className="text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} Istari Digital, Inc. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </ThemeProvider>
  );
}
